import { CancelablePromise as Promise } from "./cancelablePromise";
import TranslatorStorage from "./translatorStorage";
import DriveApi from "./api";
import { DriveStore } from "../store/reducers";
import { IFeatureFlagsService } from "../services/featureFlags";
import { FeatureFlagName } from "./featureFlagName";
import TRANSLATIONS from "../../app/modules/module-translations";
import * as logger from "./logger";
import { TranslationSource, UntranslatedLogging } from "./helpers";
import { getHtmlElementsToTranslate, replaceOldText } from "translator-tools";

export default class Translator {

    language: string;
    private readonly api: DriveApi;
    private readonly store: DriveStore;
    private readonly untranslated: Set<string>  = new Set();

    constructor(language: string, api: DriveApi, store: DriveStore, private readonly featureFlagsService: IFeatureFlagsService){
        this.language = language;
        this.api = api;
        this.store = store;
        this.loadReactModuleTranslations();
        this.getLocalTranslation = this.getLocalTranslation.bind(this);
    }

    /**
     * Check if the language in the store is different then the active language, if so, clear storage.
     * Additionally, if redux store is being used, ensure legacy store is cleared.
     *
     * @param exceptLanguage the current active language
     */
    validateCache(exceptLanguage: string) {
        if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TRANSLATION_REDUX_STORE)){
            if (this.store.getState()?.translations.language !== exceptLanguage || this.language !== exceptLanguage) {
                TranslatorStorage.cleanUpRedux(exceptLanguage, this.store);
                // Makes sure that when we change the language, we load the correct module translations
                this.loadReactModuleTranslations();
            }
            TranslatorStorage.clearLegacyLocalStorage(exceptLanguage);
        } else {
            TranslatorStorage.cleanUp(exceptLanguage, this.store);
        }
    }

    destroy() {
        // this.api.destroy();
    }

    translate<T extends string[] | string>(text: T, language?: string): Promise<T> {
        let unwrap = function (translated) {
            if (typeof (text) === "string") {
                return translated[0];
            }

            return translated;
        };

        if ((this.language === "en") || (language && language === "en")) {
            return Promise.resolve(unwrap(this.normalizeTranslations(text)));
        }

        return this.translateSentences(new Array<string>().concat(text), language).then(
            translatedText => unwrap(translatedText),
            () => unwrap(this.markAsUntranslated(this.normalizeTranslations(text)))
        );
    }

    translateHtml(elements: HTMLElement[], language?: string): Promise<HTMLElement[]> {
        let htmlElements = new Array<HTMLElement>().concat(<HTMLElement | HTMLElement[]>elements);
        let [groupsForTranslation, sentences] = getHtmlElementsToTranslate(htmlElements, this.language || language || "en");

        if (sentences.length > 0) {
            return this.translateSentences(sentences, language).then(
                newSentences => {
                    replaceOldText(groupsForTranslation, newSentences);
                    return htmlElements;
                },
                () => htmlElements
            );
        }

        return Promise.resolve(htmlElements);
    }

    translateTemplate(template: string | Function, language?: string): Promise<HTMLElement[]> {
        let t = template;
        if (typeof (template) === "function") {
            t = template();
        }
        let elements = this.strToElements(<string>t);
        return this.translateHtml(elements, language);
    }

    /**
     * Will fetch translation from local storage in the language translator currently contains if legacy
     * storage is used. If translation redux store is used, it will fetch whatever is actively in the store.
     * @param sentence the string to translate
     * @returns translation the resulting translation
     */
    getLocalTranslation (sentence: string): string {
        let translatedString: string | undefined | null = undefined;
        if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TRANSLATION_REDUX_STORE)) {
            translatedString = this.store.getState().translations.sentenceDirectory[sentence];
        } else {
            translatedString = TranslatorStorage.get(sentence, this.language);
        }

        if (!translatedString) {
            // Note, if the string is not in local storage, it was not added to `module-translations.ts`
            // Additionally, untranslated strings due to exclusion from `translations` folder files will not be logged here
            this.logUntranslatedString(sentence);
            return sentence;
        }

        return translatedString;
    }

    getString(strings: TemplateStringsArray, ...values): Promise<string> {
        let template = strings.reduce((templ: string, str: string, index: number) => {
            let t = templ;
            if (index > 0) {
                t += `{${ index - 1 }}`;
            }
            return t + str;
        });

        return this.translate(template).then(translated => {
            return values.reduce((result, value, index) => {
                return result.replace(`{${ index }}`, value);
            }, translated);
        });
    }

    private logUntranslatedString (id: string): void{
        let store = this.store.getState();
        if (store && !this.untranslated.has(id)) {
            this.untranslated.add(id);
            let string: UntranslatedLogging = {
                untranslatedString: id,
                page: store.mobile.currentPage,
                pageState: store.mobile.pageState,
                version: store.data.clientVersion,
                source: TranslationSource.REACT_MODULE
            };
            logger.log(logger.Source.UNTRANSLATED_STRING, `Untranslated String: ${ JSON.stringify(string) }`);
        }
    }

    private loadReactModuleTranslations (): void {
        this.translate(TRANSLATIONS)
        .then(() => logger.log(logger.Source.UI, `Loaded Module Translations`))
        .catch((error) => logger.error(logger.Source.ERROR, `Error when loading react module translations, error: ${ error.message }, with language: ${ this.language }`));
    }

    private markAsUntranslated(text: string[]): string[] {
        return text.map(v => `**${ v }`);
    }

    private normalizeTranslations(sentences: string | string[]) {
        return typeof sentences === "string" ? [sentences] : sentences;
    }

    private translateSentences(sentences: string[], language?: string): Promise<string[]> {
        let lang = language || this.language;
        if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TRANSLATION_REDUX_STORE)) {
            return TranslatorStorage.translateSentencesRedux(this.api, sentences, lang, this.store);
        }
        return TranslatorStorage.translateSentences(this.api, sentences, lang, this.store);
    }

    private strToElements(template: string): HTMLElement[] {
        let tempDiv = document.createElement("div");
        tempDiv.innerHTML = template;
        return Array.prototype.slice.call(tempDiv.childNodes);
    }
}
